import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";

export const REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🎉", "🔥", "👏", "✅", "🙏"];
export const msgKey = (cid) => ["chat-messages", cid];

export const upsertMessage = (qc, msg) => qc.setQueryData(msgKey(msg.channel_id), (old) => {
  if (!old) return old;
  if (old.pages.some((p) => p.items.some((m) => m.id === msg.id))) {
    return { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.map((m) => (m.id === msg.id ? msg : m)) })) };
  }
  const [first, ...rest] = old.pages;
  return { ...old, pages: [{ ...first, items: [...first.items, msg] }, ...rest] };
});

export const useChannels = (polling = false) => useQuery({
  queryKey: ["chat-channels"],
  refetchInterval: polling ? 30000 : false,
  queryFn: () => api.get("/chat/channels").then((r) => r.data),
});

export const useMembers = (cid) => useQuery({
  queryKey: ["chat-members", cid], enabled: !!cid, staleTime: 5 * 60 * 1000,
  queryFn: () => api.get(`/chat/channels/${cid}/members`).then((r) => r.data),
});

export const useMessages = (cid) => useInfiniteQuery({
  queryKey: msgKey(cid),
  enabled: !!cid,
  initialPageParam: null,
  staleTime: Infinity,
  queryFn: ({ pageParam }) => api.get(`/chat/channels/${cid}/messages`, { params: pageParam ? { before: pageParam } : {} }).then((r) => r.data),
  getNextPageParam: (last) => (last.has_more ? last.items[0]?.created_at : undefined),
});

const useMessageMutation = (fn) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (msg) => { upsertMessage(qc, msg); qc.invalidateQueries({ queryKey: ["chat-channels"] }); },
    onError: (e) => toast.error(errorMessage(e)),
  });
};

export const useSendMessage = (cid) => useMessageMutation((body) => api.post(`/chat/channels/${cid}/messages`, body).then((r) => r.data));
export const useEditMessage = () => useMessageMutation(({ id, text }) => api.patch(`/chat/messages/${id}`, { text }).then((r) => r.data));
export const useDeleteMessage = () => useMessageMutation((id) => api.delete(`/chat/messages/${id}`).then((r) => r.data));
export const useReact = () => useMessageMutation(({ id, emoji }) => api.post(`/chat/messages/${id}/reactions`, { emoji }).then((r) => r.data));

export const useMarkRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cid) => api.post(`/chat/channels/${cid}/read`),
    onSuccess: (_, cid) => qc.setQueryData(["chat-channels"], (old) => old?.map((c) => (c.id === cid ? { ...c, unread: 0 } : c))),
  });
};

export const chatFolder = (qc, cid) => qc.fetchQuery({
  queryKey: ["chat-folder", cid], staleTime: 10 * 60 * 1000,
  queryFn: () => api.get(`/chat/channels/${cid}/folder`).then((r) => r.data.folder_id),
});
